import { STAGE_INFO, ProjectStage } from '../types/projects';
import { stageLabelForSide } from '../contract-administration/roleProfile';
import {
  Mail,
  Play,
  UserCheck,
  FileEdit,
  Send,
  Handshake,
  Hammer,
  Receipt,
  Clock,
  CheckCircle2,
  TrendingUp,
  Pause
} from '@/shared/ui/icons';

// ─── Dynamic label/colour/icon overlay ───────────────────────────────────────
// Populated by ProjectStagesService.load() from the project_stages DB table after
// first fetch. Keyed by canonical stage key. When present, an overlay entry takes
// precedence over the hard-coded STAGE_INFO so a label/colour can be edited in the
// database without a code change. STAGE_INFO remains the synchronous fallback until
// (and if) the table loads, so every consumer below stays synchronous.
type StageOverlayEntry = { label?: string; color?: string; icon?: string };
const _stageOverlay = new Map<string, StageOverlayEntry>();

export function applyStageOverlay(
  stages: Array<{ key: string; label?: string | null; color?: string | null; icon?: string | null }>,
): void {
  _stageOverlay.clear();
  for (const s of stages) {
    if (!s.key) continue;
    _stageOverlay.set(s.key, {
      label: s.label ?? undefined,
      color: s.color ?? undefined,
      icon: s.icon ?? undefined,
    });
  }
}

/**
 * Standard QS/contracting project lifecycle in chronological order.
 *
 * Domain note: "client" in this product is the Contractor we work for
 * (they're our customer), and "Employer" is the end customer the
 * Contractor ultimately submits the tender to. So `client_review` is
 * an internal-to-Contractor sign-off step that happens BEFORE
 * `ready_to_submit` (Contractor's signed off, package can go to the
 * Employer) and `submitted` (package has gone to the Employer).
 *
 * Pre-award: enquiry → preparing tender → Contractor reviews →
 * cleared to send → submitted to Employer.
 * Post-award: contracts signed → work on site → settling money →
 * retention / DLP → fully closed.
 * `on_hold` is a state that can apply at any point.
 */
export const ORDERED_STAGES: ProjectStage[] = [
  'itt_received',
  'tendering',
  'tender_pending_review',
  'internal_review',
  'cleared_for_submission',
  'tender_submitted',
  'on_hold',
  'pre_start',
  'on_site',
  'practical_completion',
  'final_account',
  'defects_liability',
  'closed'
];

/**
 * Operational priority for the project register.
 *
 * The canonical ORDERED_STAGES above must remain chronological because it is
 * also used by stage progression and lifecycle views. The project register is
 * a working list, so it surfaces live delivery and imminent mobilisation
 * first, followed by close-out work, the tender pipeline, and inactive work.
 */
export const PROJECT_LIST_STAGE_ORDER: ProjectStage[] = [
  'on_site',
  'pre_start',
  'itt_received',
  'tendering',
  'tender_pending_review',
  'internal_review',
  'cleared_for_submission',
  'tender_submitted',
  'practical_completion',
  'final_account',
  'defects_liability',
  'on_hold',
  'closed',
];

export function normalizeProjectStage(stage: string | null | undefined): ProjectStage {
  if (!stage) return 'tendering';

  const normalized = stage.toLowerCase().replace(/-/g, '_');
  const stageMap: Record<string, ProjectStage> = {
    // Canonical IDs — these match the DB CHECK constraint and the
    // ProjectStage type union exactly.
    itt_received:           'itt_received',
    tender_pending_review:  'tender_pending_review',
    tendering:              'tendering',
    internal_review:        'internal_review',
    cleared_for_submission: 'cleared_for_submission',
    tender_submitted:       'tender_submitted',
    on_hold:                'on_hold',
    pre_start:              'pre_start',
    on_site:                'on_site',
    practical_completion:   'practical_completion',
    final_account:          'final_account',
    defects_liability:      'defects_liability',
    closed:                 'closed',

    // Legacy IDs (pre-rename) and informal aliases. Mapping them back
    // to canonical IDs means any stale stored value, any legacy
    // analytics dump, or any pre-deploy edge-function write still
    // displays correctly.
    enquiry_received:      'itt_received',
    new_enquiry:           'itt_received',
    tender_in_progress:    'tendering',
    in_progress:           'tendering',
    client_review:         'internal_review',
    with_client:           'internal_review',
    ready_to_submit:       'cleared_for_submission',
    finalising_submission: 'cleared_for_submission',
    submitted:             'tender_submitted',
    pre_contract:          'pre_start',
    mobilisation:          'pre_start',
    construction:          'on_site',
    pc:                    'practical_completion',
    practical_complete:    'practical_completion',
    retention_period:      'defects_liability',
    retention:             'defects_liability',
    dlp:                   'defects_liability',
    complete:              'closed',
  };

  return stageMap[normalized] ?? 'tendering';
}

/**
 * Get color classes for a project stage badge
 * Returns text, background, and border colors for light and dark modes
 */
export function getStageColor(stage: ProjectStage | string): string {
  // Normalise first so legacy IDs (e.g. 'client_review',
  // 'tender_in_progress') resolve to canonical entries in STAGE_INFO.
  // Belt-and-braces: keep the 'tendering' fallback for any value
  // that even normalize can't recognise.
  const canonical = normalizeProjectStage(stage);
  const info = STAGE_INFO[canonical] || STAGE_INFO['tendering'];
  const colorName = _stageOverlay.get(canonical)?.color || info.color;

  const colors: Record<string, string> = {
    blue:   'text-status-info-foreground bg-status-info-bg',
    red:    'text-status-danger-foreground bg-status-danger-bg',
    rose:   'text-status-danger-foreground bg-status-danger-bg',
    green:  'text-status-success-foreground bg-status-success-bg',
    amber:  'text-status-warning-foreground bg-status-warning-bg',
    yellow: 'text-status-warning-foreground bg-status-warning-bg',
    orange: 'text-status-warning-foreground bg-status-warning-bg',
    purple: 'text-primary bg-nav-item-active-bg',
    indigo: 'text-primary bg-nav-item-active-bg',
    gray:   'text-muted-foreground bg-sidebar-accent',
  };

  return colors[colorName] || colors.gray;
}

/**
 * Get the icon component for a project stage
 */
export function getStageIcon(stage: ProjectStage | string) {
  const canonical = normalizeProjectStage(stage);
  const info = STAGE_INFO[canonical] || STAGE_INFO['tendering'];
  const iconName = _stageOverlay.get(canonical)?.icon || info.icon;

  const icons: Record<string, any> = {
    Mail,
    Play,
    UserCheck,
    FileEdit,
    Send,
    Pause,
    Handshake,
    Hammer,
    Receipt,
    Clock,
    CheckCircle2,
  };

  const IconComponent = icons[iconName] || TrendingUp;
  return IconComponent;
}

/**
 * Get the label for a project stage
 */
export function getStageLabel(stage: ProjectStage | string): string {
  // Normalise legacy IDs (e.g. 'client_review' → 'internal_review')
  // before looking up the canonical label, otherwise the dashboard
  // would render a stale label for stale stored stage values.
  const canonical = normalizeProjectStage(stage);
  const overlaid = _stageOverlay.get(canonical)?.label;
  if (overlaid) return overlaid;
  const info = STAGE_INFO[canonical];
  return info ? info.label : formatStageLabel(stage);
}

/**
 * Stage label for a specific project. Employer-side projects share the
 * stored stage values but read them from the Employer's point of view, so
 * their pre-contract stages take the rule set's labels ahead of the
 * contractor-side overlay.
 */
export function getStageLabelForProject(stage: ProjectStage | string, actingFor?: string | null): string {
  return stageLabelForSide(normalizeProjectStage(stage), actingFor) ?? getStageLabel(stage);
}

/**
 * Format a stage string into a display label
 * Handles special cases like 'on_hold' → 'On Hold'
 */
export function formatStageLabel(stage: string): string {
  if (!stage) return 'Not Set';

  // Canonical IDs (post-rename) plus a wide net of aliases for any
  // stale stored value. Every alias maps to its current canonical
  // label so the UI never renders raw snake_case.
  const stageLabels: Record<string, string> = {
    // Canonical
    itt_received:           'Tender Received',
    tender_pending_review:  'QC Check',
    tendering:              'In Progress',
    internal_review:        'Client Review',
    cleared_for_submission: 'Cleared for Submission',
    tender_submitted:       'Tender Submitted',
    on_hold:                'On Hold',
    pre_start:              'Pre-Start',
    on_site:                'On Site',
    practical_completion:   'Practical Completion',
    final_account:          'Final Account',
    defects_liability:      'Defects Liability (DLP)',
    closed:                 'Closed',

    // Legacy IDs (pre-rename) — still present in any cached payload
    // or pre-deploy edge-function write.
    enquiry_received:      'Tender Received',
    new_enquiry:           'Tender Received',
    tender_in_progress:    'In Progress',
    in_progress:           'In Progress',
    client_review:         'Client Review',
    with_client:           'Client Review',
    ready_to_submit:       'Cleared for Submission',
    finalising_submission: 'Cleared for Submission',
    submitted:             'Tender Submitted',
    pre_contract:          'Pre-Start',
    mobilisation:          'Pre-Start',
    construction:          'On Site',
    pc:                    'Practical Completion',
    practical_complete:    'Practical Completion',
    retention_period:      'Defects Liability (DLP)',
    retention:             'Defects Liability (DLP)',
    dlp:                   'Defects Liability (DLP)',
    complete:              'Closed',
    On_Hold:               'On Hold',
    'on-hold':             'On Hold',
    'on-site':             'On Site',
    'On-Site':             'On Site',
  };

  // Normalize common variants
  const normalized = stage.toLowerCase().replace(/-/g, '_');

  return stageLabels[stage] || stageLabels[normalized] || normalized
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get badge variant based on stage color
 * Maps stage colors to badge variants for consistency
 */
export function getStageBadgeVariant(
  stage: ProjectStage | string
): 'default' | 'secondary' | 'outline' | 'destructive' {
  const info = STAGE_INFO[stage as ProjectStage];
  if (!info) return 'outline';

  switch (info.color) {
    case 'green':
      return 'default';
    case 'red':
    case 'rose':
      return 'destructive';
    case 'gray':
      return 'secondary';
    default:
      return 'outline';
  }
}

/**
 * Get simple background color for stage indicator dots
 * Returns just the background color class (e.g., 'bg-blue-500')
 */
export function getStageDotColor(stage: ProjectStage | string): string {
  const canonical = normalizeProjectStage(stage);
  const info = STAGE_INFO[canonical] || STAGE_INFO['tendering'];
  const colorName = _stageOverlay.get(canonical)?.color || info.color;

  const colors: Record<string, string> = {
    blue: 'bg-blue-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    indigo: 'bg-indigo-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
    gray: 'bg-gray-500',
    red: 'bg-red-500',
  };

  return colors[colorName] || colors.gray;
}
